import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { useLocation } from 'react-router-dom';
import { prefersReducedMotion } from '@/theme/reducedMotion';

/**
 * Symbolic navigation indicator. It does not track in-flight queries.
 * Remounting on pathname avoids a synchronous setState in the effect:
 * the bar starts visible and hides itself after 300ms.
 */
export function TopProgressBar(): JSX.Element | null {
  const location = useLocation();
  return <RouteProgress key={location.pathname} />;
}

function RouteProgress(): JSX.Element | null {
  const [loading, setLoading] = useState(true);
  const reduce = prefersReducedMotion();

  useEffect(() => {
    const timer = window.setTimeout(() => setLoading(false), 300);
    return () => window.clearTimeout(timer);
  }, []);

  if (!loading) return null;

  return (
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        zIndex: (theme) => theme.zIndex.tooltip + 1,
        pointerEvents: 'none',
        bgcolor: reduce ? 'primary.main' : 'transparent',
        ...(reduce
          ? {}
          : {
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                bgcolor: 'primary.main',
                animation: 'progressBar 300ms ease-out forwards',
              },
              '@keyframes progressBar': {
                '0%': { transform: 'translateX(-100%)' },
                '60%': { transform: 'translateX(-20%)' },
                '100%': { transform: 'translateX(0)' },
              },
            }),
      }}
    />
  );
}
